/**
 * Decoding an Item Code (#1271).
 *
 * An 8-digit Item Code is SS TT UUUU: supplier, product type, and the barcode's
 * 4-digit UID (`0312` + `1729` is LITWIS, Static Profile, UID 1729). The tables
 * are copied from the PD SharePoint workbook
 * `SOPs/ItemCode_Assignment-Vendor List_Inv-Locations_GL-Codes.xlsx`, sheet
 * VENDOR CODES (columns C and F), as of 2026-07-21. Purchasing owns that
 * workbook: when it changes, change these tables to match.
 */
/** Product type: the 3rd and 4th digits. */
export const ITEM_CODE_TYPES = {
    '01': 'Moving Yoke',
    '02': 'Scanner',
    '03': 'Static Wash',
    '04': 'Followspot',
    '05': 'Effect',
    '06': 'Laser',
    '07': 'Atmospherics',
    '08': 'Controller',
    '09': 'Display',
    '10': 'Truss',
    '11': 'Accessory',
    '12': 'Static Profile',
    '13': 'Studio Fixture',
    '14': 'Networking',
    '51': 'Indoor - Ilumiline',
    '52': 'Indoor - Ilumipanel',
    '53': 'Indoor - Ilumipod',
    '54': 'Indoor - Control',
    '55': 'Indoor - Accessory',
    '61': 'Outdoor - Ilumiline',
    '62': 'Outdoor - Ilumipanel',
    '63': 'Outdoor - Ilumipod',
    '64': 'Outdoor - Control',
    '65': 'Outdoor - Accessory',
    '99': 'Other',
    // In use on products but missing from the workbook (measured 2026-10-21):
    // 19 on chargers and COLORado Panel filters, 34 on hazers, 91 on a lamp.
    // Accepted so existing codes stay valid until purchasing names them.
    '19': 'Unnamed type 19',
    '34': 'Unnamed type 34',
    '91': 'Unnamed type 91',
};
/** Supplier: the 1st and 2nd digits. Many minor suppliers share 99. */
export const ITEM_CODE_SUPPLIERS = {
    '01': 'GUANGZ',
    '02': 'KINPOI',
    '03': 'LITWIS',
    '04': 'TOPLED',
    '05': 'DJPOWE',
    '06': 'ACME',
    '07': 'C&WPRO',
    '08': 'CINSTA',
    '09': 'GUABIR',
    '10': 'CRPHOT',
    '11': 'EASDIG',
    '12': 'ABOUTS',
    '13': 'ALUTRU',
    '14': 'GRIGNA',
    '15': 'GUAKON',
    '16': 'JYUYAO',
    '17': 'ALKLED',
    '19': 'CALBEN',
    '20': 'SHECRE',
    '21': 'XIARGB',
    '22': 'MOUNTE',
    '23': 'LEDOPT',
    '24': 'CHAMSYS',
    '25': 'GUAABL',
    // The workbook lists both under 26.
    '26': 'KINOFLO / NOVTEC',
    '27': 'ROCCUS',
    '28': 'SHEINF',
    '99': 'Other supplier',
};
/** Decode the first four digits of an Item Code or a bare prefix; null when there aren't four. */
export function decodeItemCode(code) {
    const prefix = String(code ?? '').trim().slice(0, 4);
    if (!/^\d{4}$/.test(prefix))
        return null;
    const supplierCode = prefix.slice(0, 2);
    const typeCode = prefix.slice(2);
    return {
        supplierCode,
        supplier: ITEM_CODE_SUPPLIERS[supplierCode] ?? null,
        typeCode,
        type: ITEM_CODE_TYPES[typeCode] ?? null,
    };
}
/** "LITWIS · Static Profile", or what's missing. For display beside a code. */
export function describeItemCode(code) {
    const d = decodeItemCode(code);
    if (!d)
        return null;
    return `${d.supplier ?? `Unknown supplier ${d.supplierCode}`} · ${d.type ?? `Unknown type ${d.typeCode}`}`;
}
/**
 * The options of the product's "Item Code Type" field: "12 - Static Profile".
 * The stored value is the whole option, so the guard reads its first two digits.
 * ponytail: renaming a type changes its option string, so a rename needs a
 * migration that rewrites the stored values too, or those products 400 on save.
 */
export const ITEM_CODE_TYPE_OPTIONS = Object.entries(ITEM_CODE_TYPES)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([code, label]) => `${code} - ${label}`);
/** The option for a type code, e.g. "12" → "12 - Static Profile". */
export function itemCodeTypeOption(typeCode) {
    const label = ITEM_CODE_TYPES[typeCode];
    return label ? `${typeCode} - ${label}` : null;
}
/**
 * Why an Item Code can't be saved on a product, or null.
 *
 * `codeChanged`: only a new or changed code must decode, so a legacy code
 * doesn't block an unrelated save. `typeOption`: the product's Item Code Type;
 * when both are set, the code's type digits must match it. That is the check
 * that catches an ellipsoidal given a wash code even when product and barcode
 * agree (#1267).
 */
export function itemCodeTypeProblem(code, typeOption, codeChanged) {
    const trimmed = String(code ?? '').trim();
    if (!/^\d{8}$/.test(trimmed))
        return null;
    const d = decodeItemCode(trimmed);
    if (codeChanged && !d.supplier) {
        return `Item Code ${trimmed} starts with supplier ${d.supplierCode}, which isn't in the Item Code list.`;
    }
    if (codeChanged && !d.type) {
        return `Item Code ${trimmed} has product type ${d.typeCode}, which isn't in the Item Code list.`;
    }
    const want = String(typeOption ?? '').trim().slice(0, 2);
    if (/^\d{2}$/.test(want) && want !== d.typeCode) {
        return (`Item Code ${trimmed} is type ${d.typeCode} (${d.type ?? 'unknown'}), but this product's Item Code Type is ` +
            `${typeOption}. Change one of them so they agree.`);
    }
    return null;
}
//# sourceMappingURL=item-code.js.map